import Dashboard from './dashboard.vue';
import OebMenu from './oeb-menu.vue';
import ControlCifrasOeb from './control-cifras-oeb.vue';
import OebResumenAnual from './oeb-resumen-anual.vue';
import OebConfig from './oeb-config.vue';
import ControlCifrasOebPlan from './control-cifras-oeb-planificacion.vue';
import OebProductividad from './control-cifras-oeb-productividad.vue';
import OebPlanDiaria from './control-cifras-oeb-planificacion-diaria.vue';
import OebProdPlan from './control-cifras-oeb-prod-plan.vue';
import OebEjercicios from './oeb-ejercicios.vue';
import OebAnalisisDiferencias from './oeb-analisis-diferencias.vue';
import NotificacionesOeb from './notificaciones-oeb.vue';
import OebProdPlanNotif from './notificaciones-oeb-prodplan.vue';
import OeaMenu from './oea-menu.vue';
import ControlCifras from './control-cifras.vue';
import OeaConfig from './oea-config.vue';
import OeaResumenAnual from './oea-resumen-anual.vue';
import OeaEjercicios from './oea-ejercicios.vue';
import NotificacionesOea from './notificaciones-oea.vue';
import ControlPlanificacionPage from './control-planificacion.vue';
import InformePropuestaPage from './informe-propuesta.vue';
import InformesPendientesPage from './informes-pendientes.vue';
import AgenteAprendizajePage from './agente-aprendizaje.vue';
import FacturacionMenu from './facturacion-menu.vue';
import FacturacionEjercicios from './facturacion-ejercicios.vue';
import FacturacionControlDiario from './facturacion-control-diario.vue';
import FacturacionAutorizaciones from './facturacion-autorizaciones.vue';
import CentroCobros from './centro-cobros.vue';
import BandejaAsignacionCobros from './bandeja-asignacion-cobros.vue';
import MiTrabajoHoy from './mi-trabajo-hoy.vue';
import MiTrabajoSinLista from './mi-trabajo-sin-lista.vue';
import InformeAbonosCobros from './informe-abonos-cobros.vue';
import PermisosFacturacion from './permisos-facturacion.js';
import PermisosCobros from './permisos-cobros.js';
import { ROLES_CONTROL_PLANIFICACION, RUTA_CONTROL_PLANIFICACION } from './permisos-control-planificacion.js';
import { ROLES_AUTORIZACIONES, RUTA_AUTORIZACIONES } from './permisos-autorizaciones.js';
import Error403 from './../403.vue';
import FactCobro from './../../js/db/FactCobro.js';
import RoutesUtils from './../../js/RoutesUtils.js';
import { conUsuarioActualizado } from './../../js/barreraUsuarioActualizado.js';
import { crearBarrera, textoDelFallo } from './../../js/barreraRuta.js';

/**
 * La pestaña FACTURACIÓN (#171) entra por la barrera de usuario actualizado y no por
 * `RoutesUtils.checkPermission`, por dos motivos que van juntos:
 *
 *  1. El permiso es una **restricción por rol** (D-ROL: solo ADMIN y DIRECTOR) y `checkPermission`
 *     hace mandar al menú de Mongo, que da `/dashboard/` a más roles. El porqué entero, en
 *     `permisos-facturacion.js`.
 *  2. Se decide con el `Usuario.me()` de **esta** carga y no con el menú que quedó guardado de la
 *     sesión anterior. Son las cifras de facturación de la empresa: decidir eso con un permiso
 *     viejo es el fallo que cerró la #65. El porqué, en `barreraUsuarioActualizado.js`.
 */
function pantallaFacturacion(Comp) {
    return conUsuarioActualizado(function(routeTo, usuario) {
        return PermisosFacturacion.verPestana(usuario)
            ? { component: Comp }
            : { component: Error403 };
    });
}

/**
 * El Centro de Cobros · La cartera (#187), que es lo que hay detrás de la tarjeta OED · Cobros.
 *
 * Entra por la misma barrera de usuario actualizado y por el mismo motivo —se decide con el
 * `Usuario.me()` de ESTA carga, no con el menú que quedó de la sesión anterior—, pero con **otra
 * lista de roles**: la del Centro de Cobros, que es más ancha porque D-COB-5 no niega la entrada,
 * recorta. El porqué entero, en `permisos-cobros.js`.
 *
 * Y la ruta contesta a la MISMA pregunta que la tarjeta del Cuadro de mandos y con el MISMO
 * usuario, para que no puedan decir cosas distintas. Las dos son fail-closed y detrás está el
 * recorte de la API.
 */
function pantallaCobros(Comp) {
    return conUsuarioActualizado(function(routeTo, usuario) {
        if (!PermisosCobros.verCentroCobros(usuario)) {
            return { component: Error403 };
        }
        return {
            component: Comp,
            // #193 · si el informe de abonos no es alcanzable para esta persona, su enlace no se
            // dibuja: un botón que lleva a un 403 hace creer que le han quitado permisos. La
            // pregunta la contesta `permisos-cobros.js` —no la pantalla— y con el usuario de ESTA
            // carga, para que el enlace y la ruta del informe no puedan decir cosas distintas. Es
            // el mismo patrón que `puedeAbrirFicha` del Mapa de aprobación (#65).
            props: { puedeVerInformeAbonos: PermisosCobros.verInformeAbonos(usuario) }
        };
    });
}

/**
 * El informe <b>Abonos abiertos</b> (COB-7, #193), que cuelga del Centro de Cobros.
 *
 * Entra por la misma barrera de usuario actualizado y con **otra lista de roles**: aquí D-COB-5 no
 * recorta, cierra —los abonos abiertos son de la empresa entera y un comercial no tiene una parte
 * suya—, así que quien no esté en la lista ve el 403 y no un informe vacío. El porqué, en
 * `permisos-cobros.js`.
 *
 * Y la ruta contesta a la MISMA pregunta que el enlace del Centro de Cobros y con el MISMO usuario.
 * Las dos son fail-closed y detrás está el 403 de la API, que es el que manda.
 */
function pantallaInformeAbonos(Comp) {
    return conUsuarioActualizado(function(routeTo, usuario) {
        return PermisosCobros.verInformeAbonos(usuario)
            ? { component: Comp }
            : { component: Error403 };
    });
}

/**
 * «Mi trabajo de hoy» (COB-4, #190): la lista diaria de gestión de cobro.
 *
 * Entra por una barrera, igual que el resto de las pantallas que deciden algo con un permiso, pero
 * **el permiso lo contesta el servidor y no el usuario que trae `Usuario.me()`**. El motivo es que
 * quién entra no es una lista de roles: entran Administración, Dirección y Administrador, y entra
 * **cada gestor de cobro** con su cartera (criterio 10); y «gestor de cobro» no es un rol del
 * sistema, es quien figura como PGC en alguna factura (D-COB-3), un dato que el navegador no conoce.
 * Un comercial no entra, y un comercial al que mañana le asignen cartera sí.
 *
 * O sea que esto NO es `pantallaCobros` con otra lista: es la misma mecánica de arranque
 * (`barreraRuta.js` — tope de tiempo, descarte de la navegación abandonada, cerrojo del router) con
 * otra pregunta, `FactCobro.accesoTrabajoHoy()`. Y es **la misma pregunta** que hace la pantalla del
 * Centro de Cobros para decidir si dibuja el enlace, para que no pueda enseñar una puerta que esto
 * cierra: la regla de dirección del 05/10/2026 dice que un botón que lleva a un 403 es peor que no
 * tener botón, porque hace creer que te han quitado permisos.
 *
 * **Fail-closed en las tres salidas**: si no entra, si el servidor no contesta y si contesta algo
 * que no se sabe leer. Una respuesta sin el `puedeEntrar` booleano no es un permiso.
 */
function pantallaTrabajoHoy() {
    return crearBarrera({
        pedir: function() {
            return FactCobro.accesoTrabajoHoy();
        },
        alResolver: function(acceso, routeTo) {
            if (!acceso || typeof acceso.puedeEntrar != 'boolean') {
                return {
                    component: MiTrabajoSinLista,
                    props: {
                        url: routeTo.url,
                        reintentable: true,
                        motivo: 'El servidor ha contestado algo que no se ha sabido leer, así que no '
                            + 'se sabe si tienes lista de cobro.'
                    }
                };
            }
            if (!acceso.puedeEntrar) {
                return {
                    component: MiTrabajoSinLista,
                    props: {
                        url: routeTo.url,
                        reintentable: false,
                        // El motivo lo escribe el servidor: depende del dato —no tener cartera
                        // asignada— y no de los roles, así que una frase escrita aquí acabaría
                        // diciendo «no tienes permiso» a quien lo que le falta es que alguien le
                        // asigne facturas.
                        motivo: acceso.motivo || 'No tienes ninguna factura asignada como gestor de '
                            + 'cobro, así que no hay ninguna lista que enseñarte.'
                    }
                };
            }
            return {
                component: MiTrabajoHoy,
                props: { veTodaLaCartera: !!acceso.veTodaLaCartera }
            };
        },
        alFallar: function(e, routeTo) {
            return {
                component: MiTrabajoSinLista,
                props: {
                    url: routeTo.url,
                    reintentable: true,
                    motivo: textoDelFallo(e, 'No se ha podido comprobar tu lista de cobro.')
                }
            };
        }
    });
}

const routes = [
    {
        path: '/dashboard/',
        beforeEnter: RoutesUtils.checkAuthPage,
        async: RoutesUtils.checkPermission('dashboard', ['ADMIN'], Dashboard)
    },
    {
        // SPEC-DASHBOARD-CONTROL-PLANIFICACION: dashboard de control del planificado (EST PLA)
        //
        // #326 · la lista de roles sale de `permisos-control-planificacion.js`, que es la misma que
        // mira la tarjeta de OEB Producción. Antes estaba escrita aquí y la tarjeta no existía; en
        // cuanto hay un punto de entrada, tener dos copias es cómo se acaba dibujando un botón que
        // lleva a un 403 (el precedente, en `permisos-empleados.js`).
        path: RUTA_CONTROL_PLANIFICACION,
        beforeEnter: RoutesUtils.checkAuthPage,
        async: RoutesUtils.checkPermission('dashboard/control-planificacion', ROLES_CONTROL_PLANIFICACION, ControlPlanificacionPage)
    },
    {
        // Agente IA F1 · panel de doble control de Dirección Técnica (ver Y aprobar/rechazar = ADMIN/DIRECTOR/DTYCAL).
        path: '/dashboard/agente-aprendizaje/',
        beforeEnter: RoutesUtils.checkAuthPage,
        async: RoutesUtils.checkPermission('dashboard/agente-aprendizaje', ['ADMIN', 'DIRECTOR', 'DTYCAL'], AgenteAprendizajePage)
    },
    {
        // SPEC-INFORME-PROPUESTA: informe de una corrida del planificador (registro auditado) + histórico
        path: '/dashboard/informe-propuesta/:registroId?/',
        beforeEnter: RoutesUtils.checkAuthPage,
        async: RoutesUtils.checkPermission('dashboard/informe-propuesta', ['ADMIN', 'PLANIFICADOR'], InformePropuestaPage)
    },
    {
        // Panel "Situación de informes pendientes (TPI) por inspector" — read-only, dato materializado.
        path: '/dashboard/informes-pendientes/',
        beforeEnter: RoutesUtils.checkAuthPage,
        async: RoutesUtils.checkPermission('dashboard/informes-pendientes', ['ADMIN', 'PLANIFICADOR'], InformesPendientesPage)
    },
    {
        // FACT-COBRO F3 (#171) · la pestaña FACTURACIÓN y, dentro, la Tarjeta B. La Tarjeta A ·
        // control diario es F2 (#170) y cuelga de aquí con el mismo permiso.
        path: '/dashboard/facturacion/',
        beforeEnter: RoutesUtils.checkAuthPage,
        async: pantallaFacturacion(FacturacionMenu)
    },
    {
        path: '/dashboard/facturacion/ejercicios/',
        beforeEnter: RoutesUtils.checkAuthPage,
        async: pantallaFacturacion(FacturacionEjercicios)
    },
    {
        // FACT-COBRO F2 (#170) · Tarjeta A, el control diario. Mismo permiso que el resto de la
        // pestaña y por el mismo camino: es la cuarta pregunta que `permisos-facturacion.js`
        // contesta, y la contesta igual que las otras tres.
        path: '/dashboard/facturacion/control-diario/',
        beforeEnter: RoutesUtils.checkAuthPage,
        async: pantallaFacturacion(FacturacionControlDiario)
    },
    {
        // #905 · OEE › Autorizaciones. Entra por el menú de Mongo y no por `pantallaFacturacion`:
        // quienes autorizan (las Aceleradoras) no tienen por qué tener el rol de FACTURACIÓN, y el
        // permiso que importa —«autoriza»— lo comprueba la API en cada decisión. La tarjeta y el
        // enlace del bloque de condiciones hacen la misma pregunta (`permisos-autorizaciones.js`).
        path: RUTA_AUTORIZACIONES,
        beforeEnter: RoutesUtils.checkAuthPage,
        async: RoutesUtils.checkPermission('dashboard/facturacion/autorizaciones', ROLES_AUTORIZACIONES, FacturacionAutorizaciones)
    },
    {
        // FACT-COBRO · COB-1 (#187) · Centro de Cobros · La cartera. Es la pantalla de la tarjeta
        // OED · Cobros del Cuadro de mandos, que esa tarea enciende. NO cuelga de la pestaña
        // FACTURACIÓN ni comparte su permiso: son dos cosas distintas (ver `pantallaCobros`).
        path: '/dashboard/cobros/',
        beforeEnter: RoutesUtils.checkAuthPage,
        async: pantallaCobros(CentroCobros)
    },
    {
        // FACT-COBRO · COB-7 (#193) · el informe «Abonos abiertos» para Administración. Es de SOLO
        // LECTURA: lista los abonos con Estado = Abierta y sugiere con qué factura casaría cada uno,
        // sin casar nada (D-COB-9). Se llega desde el Centro de Cobros, que solo dibuja el enlace a
        // quien puede entrar (ver `pantallaCobros`).
        path: '/dashboard/cobros/abonos/',
        beforeEnter: RoutesUtils.checkAuthPage,
        async: pantallaInformeAbonos(InformeAbonosCobros)
    },
    {
        // FACT-COBRO · COB-2 (#188) · la bandeja de asignación del gestor de cobro, que es la única
        // pantalla del programa que escribe.
        //
        // SIN barrera de rol en el navegador, al contrario que el Centro de Cobros, y a propósito.
        // El criterio 6 de la #188 dice que un gestor de cobro VE la bandeja —necesita saber qué hay
        // sin dueño en su área para pedirlo— y lo que no puede es asignarse facturas. Pero «gestor
        // de cobro» no es un rol: es quien figura como PGC en alguna factura (D-COB-3), un dato que
        // el navegador no conoce. Gateando por `PermisosCobros.ROLES_ENTRADA` —que es la lista de la
        // #187— un gestor con cartera y sin ninguno de esos cuatro roles acababa en Error403 en SU
        // bandeja: hallazgo P2 de la revisión CODEX del PR #1751, y tiene razón.
        //
        // Quitarla no es abrir nada: D-COB-5 no niega la entrada, RECORTA, y ese recorte lo aplica
        // la API —`FactCobroCarteraService.alcance()` ya resuelve si eres gestor— y lo dice en
        // `alcance` en cada carga. Quién puede ASIGNAR lo decide también el servidor
        // (`puedeAsignar`) y es él quien rechaza la escritura con un 403 que esta pantalla sabe
        // leer. El motivo por el que la lista existe está escrito en `permisos-cobros.js` y es de
        // la TARJETA del Cuadro de mandos —no poner un «0 €» a quien no tiene cartera—, no de una
        // pantalla a la que solo se llega por enlace.
        //
        // LO QUE ESTO NO ARREGLA, y queda dicho: a ese mismo gestor sin rol la rejilla del Cuadro de
        // mandos no le enseña la tarjeta OED · Cobros ni, por tanto, el enlace a esta bandeja, así
        // que entra por URL o no entra. Cerrarlo pide cambiar `PermisosCobros.verCentroCobros` para
        // preguntar al servidor si eres gestor, y eso es permiso de la #187: el invariante del
        // programa dice que ningún permiso existente se cambia salvo donde la incidencia lo pida.
        // Va a tarea propia, dicho en el PR y en la incidencia.
        path: '/dashboard/cobros/asignacion/',
        beforeEnter: RoutesUtils.checkAuthPage,
        component: BandejaAsignacionCobros
    },
    {
        // FACT-COBRO · COB-4 (#190) · «Mi trabajo de hoy», la lista diaria de gestión de cobro.
        // Es la pantalla que ACTÚA: las otras dos del Centro de Cobros miden.
        //
        // Con barrera, y resuelta contra el SERVIDOR: ver `pantallaTrabajoHoy`. No cuelga del
        // permiso del Centro de Cobros (`PermisosCobros.ROLES_ENTRADA`) porque ahí entra el
        // COMERCIAL —es su cartera— y aquí no (criterio 10), y porque el gestor de cobro entra sin
        // ninguno de esos roles. Son dos decisiones distintas y por eso son dos barreras.
        path: '/dashboard/cobros/mi-trabajo/',
        beforeEnter: RoutesUtils.checkAuthPage,
        async: pantallaTrabajoHoy()
    },
    {
        path: '/dashboard/oeb/',
        beforeEnter: RoutesUtils.checkAuthPage,
        async: RoutesUtils.checkPermission('dashboard/oeb', ['ADMIN'], OebMenu)
    },
    {
        path: '/dashboard/oeb/control-cifras/',
        beforeEnter: RoutesUtils.checkAuthPage,
        async: RoutesUtils.checkPermission('dashboard/oeb/control-cifras', ['ADMIN'], ControlCifrasOeb)
    },
    {
        path: '/dashboard/oeb/resumen-anual/',
        beforeEnter: RoutesUtils.checkAuthPage,
        async: RoutesUtils.checkPermission('dashboard/oeb/resumen-anual', ['ADMIN'], OebResumenAnual)
    },
    {
        path: '/dashboard/oeb/configuracion/',
        beforeEnter: RoutesUtils.checkAuthPage,
        async: RoutesUtils.checkPermission('dashboard/oeb/configuracion', ['ADMIN'], OebConfig)
    },
    {
        path: '/dashboard/oeb/productividad/',
        beforeEnter: RoutesUtils.checkAuthPage,
        async: RoutesUtils.checkPermission('dashboard/oeb/productividad', ['ADMIN'], OebProductividad)
    },
    {
        path: '/dashboard/oeb/planificacion/',
        beforeEnter: RoutesUtils.checkAuthPage,
        async: RoutesUtils.checkPermission('dashboard/oeb/planificacion', ['ADMIN'], ControlCifrasOebPlan)
    },
    {
        path: '/dashboard/oeb/planificacion-diaria/',
        beforeEnter: RoutesUtils.checkAuthPage,
        async: RoutesUtils.checkPermission('dashboard/oeb/planificacion-diaria', ['ADMIN'], OebPlanDiaria)
    },
    {
        path: '/dashboard/oeb/prod-plan/',
        beforeEnter: RoutesUtils.checkAuthPage,
        async: RoutesUtils.checkPermission('dashboard/oeb/prod-plan', ['ADMIN'], OebProdPlan)
    },
    {
        path: '/dashboard/oeb/ejercicios/',
        beforeEnter: RoutesUtils.checkAuthPage,
        async: RoutesUtils.checkPermission('dashboard/oeb/ejercicios', ['ADMIN'], OebEjercicios)
    },
    {
        path: '/dashboard/oeb/analisis/',
        beforeEnter: RoutesUtils.checkAuthPage,
        async: RoutesUtils.checkPermission('dashboard/oeb/analisis', ['ADMIN'], OebAnalisisDiferencias)
    },
    {
        path: '/dashboard/oeb/notificaciones/',
        beforeEnter: RoutesUtils.checkAuthPage,
        async: RoutesUtils.checkPermission('dashboard/oeb/notificaciones', ['ADMIN'], NotificacionesOeb)
    },
    {
        path: '/dashboard/oeb/notificaciones-prodplan/',
        beforeEnter: RoutesUtils.checkAuthPage,
        async: RoutesUtils.checkPermission('dashboard/oeb/notificaciones-prodplan', ['ADMIN'], OebProdPlanNotif)
    },
    {
        path: '/dashboard/oea/',
        beforeEnter: RoutesUtils.checkAuthPage,
        async: RoutesUtils.checkPermission('dashboard/oea', ['ADMIN'], OeaMenu)
    },
    {
        path: '/dashboard/oea/control-cifras/',
        beforeEnter: RoutesUtils.checkAuthPage,
        async: RoutesUtils.checkPermission('dashboard/oea/control-cifras', ['ADMIN'], ControlCifras)
    },
    {
        path: '/dashboard/oea/configuracion/',
        beforeEnter: RoutesUtils.checkAuthPage,
        async: RoutesUtils.checkPermission('dashboard/oea/configuracion', ['ADMIN'], OeaConfig)
    },
    {
        path: '/dashboard/oea/resumen-anual/',
        beforeEnter: RoutesUtils.checkAuthPage,
        async: RoutesUtils.checkPermission('dashboard/oea/resumen-anual', ['ADMIN'], OeaResumenAnual)
    },
    {
        path: '/dashboard/oea/ejercicios/',
        beforeEnter: RoutesUtils.checkAuthPage,
        async: RoutesUtils.checkPermission('dashboard/oea/ejercicios', ['ADMIN'], OeaEjercicios)
    },
    {
        path: '/dashboard/oea/notificaciones/',
        beforeEnter: RoutesUtils.checkAuthPage,
        async: RoutesUtils.checkPermission('dashboard/oea/notificaciones', ['ADMIN'], NotificacionesOea)
    }
];

export default routes;
